// mcpConnectorTools.js — ponte GENÉRICA: servidores MCP conectados viram tools de
// agente reais (`mcp__{server}__{tool}`), disponíveis nas DUAS surfaces (Code e
// Chat) porque o resolver é consumido pelo mesmo `resolveExtraTools()`.
//
// Pedro (2026-08): "faça a ligação de integrações com o mcp na aba chat — não
// funciona lá". Antes, a ÚNICA tool MCP era o search_brain (obsidian), embrulhado
// à mão; os demais servidores MCP que o app consome (ex.: apolo-memory, o memory
// wiki do projeto) NUNCA viravam tool de agente em surface nenhuma. Esta ponte
// expõe qualquer servidor MCP CONECTADO de forma genérica.
//
// Contrato do resolver (igual aos outros extraTools): SEM Electron (sem
// window.apolo.mcp) → []  ⇒  conjunto de tools byte-idêntico ao de hoje.
//
// LIMITE conhecido: só servidores que o main JÁ sabe conectar (obsidian stdio +
// apolo-memory in-process). Conectores REMOTOS de terceiros (plugins com
// mcp_server_url) ainda precisam de um transporte HTTP/SSE no main (follow-up) —
// quando ele existir, este mesmo resolver os expõe sem mudança aqui.

// FQN estável e sanitizado (o provider LLM exige nome ^[a-zA-Z0-9_-]+$). Casa com
// a convenção formatMcpToolName do mcpResolver (mcp__{server}__{tool}).
export function mcpToolFqName(serverId, toolName) {
  return `mcp__${serverId}__${toolName}`.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64);
}

// Garante um JSON Schema de objeto válido pro campo `parameters` (o inputSchema
// do MCP já costuma ser um; quando ausente/inválido, um objeto permissivo).
export function normalizeMcpSchema(inputSchema) {
  if (inputSchema && typeof inputSchema === 'object' && inputSchema.type === 'object') {
    return { type: 'object', properties: inputSchema.properties || {}, ...(Array.isArray(inputSchema.required) ? { required: inputSchema.required } : {}) };
  }
  return { type: 'object', properties: {} };
}

// Achata o result do MCP ({content:[{type:'text',text}]}) numa string model-facing.
// Nunca lança; um shape inesperado é serializado como JSON (capado).
export function formatMcpToolResult(res, { cap = 6000 } = {}) {
  if (res == null) return '(sem resultado)';
  if (typeof res === 'string') return res.slice(0, cap);
  const content = Array.isArray(res?.content) ? res.content : null;
  if (content) {
    const text = content
      .map((c) => (c?.type === 'text' ? String(c.text ?? '') : (c?.text ?? (c ? JSON.stringify(c) : ''))))
      .filter(Boolean)
      .join('\n')
      .trim();
    if (text) return text.slice(0, cap);
  }
  try { return JSON.stringify(res).slice(0, cap); } catch { return String(res).slice(0, cap); }
}

/**
 * PURO — transforma servidores MCP conectados + suas tools em extraTools do loop.
 * @param {Array<{serverId:string, tools:Array<{name:string, description?:string, inputSchema?:object}>}>} servers
 * @param {{ call?: (serverId:string, toolName:string, args:object)=>Promise<any> }} deps
 * @returns {Array<{def:object, execute:Function, mutating?:boolean}>}
 */
export function buildMcpConnectorTools(servers, { call } = {}) {
  const out = [];
  const seen = new Set();
  for (const srv of Array.isArray(servers) ? servers : []) {
    const serverId = srv?.serverId;
    if (!serverId) continue;
    for (const t of Array.isArray(srv?.tools) ? srv.tools : []) {
      const toolName = t?.name;
      if (!toolName) continue;
      const fqName = mcpToolFqName(serverId, toolName);
      if (seen.has(fqName)) continue; // colisão após sanitização — mantém o 1º
      seen.add(fqName);
      out.push({
        def: {
          type: 'function',
          function: {
            name: fqName,
            description: `[MCP ${serverId}] ${t.description || toolName}`.slice(0, 1024),
            parameters: normalizeMcpSchema(t.inputSchema),
          },
        },
        execute: async (args) => {
          if (typeof call !== 'function') return `Erro: bridge MCP indisponível (rode no app Electron).`;
          try {
            return formatMcpToolResult(await call(serverId, toolName, args || {}));
          } catch (e) {
            return `Erro MCP ${serverId}/${toolName}: ${e?.message || String(e)}`;
          }
        },
      });
    }
  }
  return out;
}

// Servidores que o main sabe conectar HOJE. obsidian fica de fora: é servidor
// PESADO (child process) e já está coberto pelo search_brain — expô-lo aqui só se
// já estiver 'ready' (nunca bootar por causa da enumeração de tools por turno).
const ALWAYS_ENSURE = ['apolo-memory']; // in-process shim, barato, idempotente
const IF_READY = ['obsidian'];

/**
 * Resolve as tools dos servidores MCP conectados. Fora do Electron → [] (contrato
 * dos extraTools: OFF ⇒ tool set byte-idêntico). Injetável pra teste.
 * @param {{ mcp?: any }} [opts]
 */
export async function resolveMcpConnectorTools(opts = {}) {
  const w = /** @type {any} */ (typeof window !== 'undefined' ? window : {});
  const api = opts.mcp || w.apolo?.mcp || null;
  if (!api?.call || !api?.list) return [];

  const servers = [];
  const gather = async (serverId, { ensure = false } = {}) => {
    try {
      if (ensure && typeof api.ensureApoloMemory === 'function' && serverId === 'apolo-memory') {
        await api.ensureApoloMemory().catch(() => {}); // idempotente; falha (sem repo) → segue
      } else if (typeof api.status === 'function') {
        const st = await api.status(serverId);
        if (st?.status !== 'ready') return; // não bootar servidor pesado só p/ listar
      }
      const r = await api.list(serverId);
      const tools = Array.isArray(r?.tools) ? r.tools : [];
      if (tools.length) servers.push({ serverId, tools });
    } catch { /* servidor indisponível — ignora, não quebra o turno */ }
  };

  for (const id of ALWAYS_ENSURE) await gather(id, { ensure: true });
  for (const id of IF_READY) await gather(id);

  return buildMcpConnectorTools(servers, { call: (s, t, a) => api.call(s, t, a) });
}
